<script setup lang="ts">
import maplibregl from 'maplibre-gl'
import { inject, onUnmounted, shallowRef, watch, type ShallowRef } from 'vue'

import { useUiStore } from '@/stores/uiStore'
import { buildTrackHoverPopupHtml, estimateHoveredDistanceM } from '@/utils/trackHoverPopup'
import { buildTrackDirectionArrowLayer } from '@/utils/trackLineLayerFactory'
import type {
  TrackProfileAnalysis,
  TrackProfileFeatureProperties,
  TrackProfileMergedSegment,
  TrackProfilePoint,
  TrackProfileVisibleRange,
} from '@/utils/trackProfileAnalysis'
import {
  buildTrackProfileLegFeatureCollection,
  findNearestTrackProfilePoint,
  partitionTrackProfileLegsByRange,
} from '@/utils/trackProfileAnalysis'
import { Z_INDEX } from '@/utils/zIndex'

defineOptions({ name: 'WorkspaceTrackProfileMetricLayer' })

const props = withDefaults(
  defineProps<{
    analysis: TrackProfileAnalysis
    cursorPoint?: TrackProfilePoint | null
    highlightedCategoryId?: string | null
    focusedSegment?: TrackProfileMergedSegment | null
    visibleRangeM?: TrackProfileVisibleRange | null
    hoverTitle?: string
    showMetricDetails?: boolean
    renderBaseLine?: boolean
    // When true, render only the cursor marker (no metric lines / highlight / focus).
    // Used to keep a position marker on the plain map route while hovering the profile.
    cursorOnly?: boolean
    hoverLayerIds?: string[]
  }>(),
  {
    showMetricDetails: false,
    renderBaseLine: true,
    cursorOnly: false,
    hoverLayerIds: () => [],
  },
)

const uiStore = useUiStore()
const mapInstance = inject<ShallowRef<maplibregl.Map | null>>('mapInstance')
const INACTIVE_LINE_SOURCE_ID = 'workspace-track-edit-metric-inactive-source'
const INACTIVE_LINE_LAYER_ID = 'workspace-track-edit-metric-inactive-layer'
const INACTIVE_LINE_ARROW_LAYER_ID = 'workspace-track-edit-metric-inactive-arrow-layer'
const LINE_SOURCE_ID = 'workspace-track-edit-metric-source'
const LINE_HALO_LAYER_ID = 'workspace-track-edit-metric-halo-layer'
const LINE_LAYER_ID = 'workspace-track-edit-metric-layer'
const LINE_ARROW_LAYER_ID = 'workspace-track-edit-metric-arrow-layer'
const CURSOR_SOURCE_ID = 'workspace-track-edit-metric-cursor-source'
const CURSOR_HALO_LAYER_ID = 'workspace-track-edit-metric-cursor-halo-layer'
const CURSOR_RING_LAYER_ID = 'workspace-track-edit-metric-cursor-ring-layer'
const CURSOR_LAYER_ID = 'workspace-track-edit-metric-cursor-layer'
const HIGHLIGHT_SOURCE_ID = 'workspace-track-edit-metric-highlight-source'
const HIGHLIGHT_HALO_LAYER_ID = 'workspace-track-edit-metric-highlight-halo-layer'
const HIGHLIGHT_LAYER_ID = 'workspace-track-edit-metric-highlight-layer'
const HIGHLIGHT_ARROW_LAYER_ID = 'workspace-track-edit-metric-highlight-arrow-layer'
const FOCUSED_LEG_SOURCE_ID = 'workspace-track-edit-metric-focused-source'
const FOCUSED_LEG_HALO_LAYER_ID = 'workspace-track-edit-metric-focused-halo-layer'
const FOCUSED_LEG_LAYER_ID = 'workspace-track-edit-metric-focused-layer'
const FOCUSED_LEG_ARROW_LAYER_ID = 'workspace-track-edit-metric-focused-arrow-layer'
const FOREGROUND_LAYER_IDS = new Set(['photo-clusters', 'photo-unclustered-point'])
const FOREGROUND_LAYER_PREFIXES = ['clusters-', 'unclustered-point-', 'cluster-count-']
const BASE_LINE_HOVER_LAYER_IDS = [
  INACTIVE_LINE_LAYER_ID,
  INACTIVE_LINE_ARROW_LAYER_ID,
  LINE_HALO_LAYER_ID,
  LINE_LAYER_ID,
  LINE_ARROW_LAYER_ID,
]
const OVERLAY_HOVER_LAYER_IDS = [
  HIGHLIGHT_HALO_LAYER_ID,
  HIGHLIGHT_LAYER_ID,
  HIGHLIGHT_ARROW_LAYER_ID,
  FOCUSED_LEG_HALO_LAYER_ID,
  FOCUSED_LEG_LAYER_ID,
  FOCUSED_LEG_ARROW_LAYER_ID,
] as const
let boundMap: maplibregl.Map | null = null
type HoverLayerHandlers = {
  enter: (event: maplibregl.MapLayerMouseEvent) => void
  move: (event: maplibregl.MapLayerMouseEvent) => void
  leave: () => void
}
const boundHoverHandlers = new Map<string, HoverLayerHandlers>()
// Track which bound hover layers the pointer is currently over. The wide
// drag-insert touch target (EDIT_TOUCH) and the thin visible metric line overlap,
// so a `mouseleave` from one must not hide the popup while another is still hovered.
const enteredHoverLayerIds = new Set<string>()
const hoverPopup = shallowRef<maplibregl.Popup | null>(null)

uiStore.suppressTourHover()

const resolveHoverLayerIds = () =>
  Array.from(
    new Set([
      ...(props.renderBaseLine ? BASE_LINE_HOVER_LAYER_IDS : []),
      ...OVERLAY_HOVER_LAYER_IDS,
      ...props.hoverLayerIds,
    ]),
  )

const syncHoverLayerListeners = (map: maplibregl.Map) => {
  unbindHoverLayerListeners(map)

  for (const layerId of resolveHoverLayerIds()) {
    const handlers: HoverLayerHandlers = {
      enter: (event) => handleHoverMouseEnter(layerId, event),
      move: (event) => handleHoverMouseMove(event),
      leave: () => handleHoverMouseLeave(layerId),
    }
    boundHoverHandlers.set(layerId, handlers)
    map.on('mouseenter', layerId, handlers.enter)
    map.on('mousemove', layerId, handlers.move)
    map.on('mouseleave', layerId, handlers.leave)
  }
}

const unbindHoverLayerListeners = (map: maplibregl.Map) => {
  for (const [layerId, handlers] of boundHoverHandlers) {
    map.off('mouseenter', layerId, handlers.enter)
    map.off('mousemove', layerId, handlers.move)
    map.off('mouseleave', layerId, handlers.leave)
  }
  boundHoverHandlers.clear()
  enteredHoverLayerIds.clear()
}

const unbindMapListeners = (map: maplibregl.Map | null | undefined) => {
  if (!map) return
  map.off('load', updateMetricLayer)
  map.off('styledata', updateMetricLayer)
  unbindHoverLayerListeners(map)
}

const removeMetricLayers = (
  map: maplibregl.Map | null | undefined,
  options: { keepCursor?: boolean } = {},
) => {
  if (!map) return
  hideHoverPopup()
  if (map.getLayer(INACTIVE_LINE_ARROW_LAYER_ID)) map.removeLayer(INACTIVE_LINE_ARROW_LAYER_ID)
  if (map.getLayer(INACTIVE_LINE_LAYER_ID)) map.removeLayer(INACTIVE_LINE_LAYER_ID)
  if (map.getSource(INACTIVE_LINE_SOURCE_ID)) map.removeSource(INACTIVE_LINE_SOURCE_ID)
  if (map.getLayer(FOCUSED_LEG_ARROW_LAYER_ID)) map.removeLayer(FOCUSED_LEG_ARROW_LAYER_ID)
  if (map.getLayer(FOCUSED_LEG_LAYER_ID)) map.removeLayer(FOCUSED_LEG_LAYER_ID)
  if (map.getLayer(FOCUSED_LEG_HALO_LAYER_ID)) map.removeLayer(FOCUSED_LEG_HALO_LAYER_ID)
  if (map.getSource(FOCUSED_LEG_SOURCE_ID)) map.removeSource(FOCUSED_LEG_SOURCE_ID)
  if (map.getLayer(HIGHLIGHT_ARROW_LAYER_ID)) map.removeLayer(HIGHLIGHT_ARROW_LAYER_ID)
  if (map.getLayer(HIGHLIGHT_LAYER_ID)) map.removeLayer(HIGHLIGHT_LAYER_ID)
  if (map.getLayer(HIGHLIGHT_HALO_LAYER_ID)) map.removeLayer(HIGHLIGHT_HALO_LAYER_ID)
  if (map.getSource(HIGHLIGHT_SOURCE_ID)) map.removeSource(HIGHLIGHT_SOURCE_ID)
  if (!options.keepCursor) {
    if (map.getLayer(CURSOR_LAYER_ID)) map.removeLayer(CURSOR_LAYER_ID)
    if (map.getLayer(CURSOR_RING_LAYER_ID)) map.removeLayer(CURSOR_RING_LAYER_ID)
    if (map.getLayer(CURSOR_HALO_LAYER_ID)) map.removeLayer(CURSOR_HALO_LAYER_ID)
    if (map.getSource(CURSOR_SOURCE_ID)) map.removeSource(CURSOR_SOURCE_ID)
  }
  if (map.getLayer(LINE_ARROW_LAYER_ID)) map.removeLayer(LINE_ARROW_LAYER_ID)
  if (map.getLayer(LINE_LAYER_ID)) map.removeLayer(LINE_LAYER_ID)
  if (map.getLayer(LINE_HALO_LAYER_ID)) map.removeLayer(LINE_HALO_LAYER_ID)
  if (map.getSource(LINE_SOURCE_ID)) map.removeSource(LINE_SOURCE_ID)
}

const resolveBeforeLayerId = (map: maplibregl.Map) => {
  const layers = map.getStyle().layers ?? []
  for (const layer of layers) {
    const layerId = layer.id
    if (
      FOREGROUND_LAYER_IDS.has(layerId) ||
      FOREGROUND_LAYER_PREFIXES.some((prefix) => layerId.startsWith(prefix))
    ) {
      return layerId
    }
  }

  return undefined
}

const resolveCursorColor = () => {
  return '#64748b'
}

const hideHoverPopup = () => {
  enteredHoverLayerIds.clear()
  if (hoverPopup.value) {
    hoverPopup.value.remove()
    hoverPopup.value = null
  }
}

const coerceFeatureNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

const getHoverFeatureProperties = (
  event: maplibregl.MapLayerMouseEvent,
): Partial<TrackProfileFeatureProperties> | null => {
  const feature = event.features?.[0]
  if (!feature?.properties) return null
  return feature.properties as Partial<TrackProfileFeatureProperties>
}

const getHoverCurrentDistanceM = (
  event: maplibregl.MapLayerMouseEvent,
  properties: Partial<TrackProfileFeatureProperties> | null,
) => {
  const feature = event.features?.[0]
  if (!feature || feature.geometry.type !== 'LineString') {
    return coerceFeatureNumber(properties?.startDistanceM)
  }

  const segmentCoordinates = feature.geometry.coordinates.filter(
    (coordinate): coordinate is [number, number] =>
      Array.isArray(coordinate) &&
      coordinate.length >= 2 &&
      typeof coordinate[0] === 'number' &&
      typeof coordinate[1] === 'number',
  )
  const segmentStartDistanceM = coerceFeatureNumber(properties?.startDistanceM) ?? 0
  const hoveredSegmentDistanceM = estimateHoveredDistanceM(segmentCoordinates, event.lngLat)

  return hoveredSegmentDistanceM === null
    ? segmentStartDistanceM
    : segmentStartDistanceM + hoveredSegmentDistanceM
}

const resolveHoveredLeg = (distanceM: number | null) => {
  if (distanceM === null || props.analysis.legs.length === 0) return null

  return (
    props.analysis.legs.find(
      (leg) => distanceM >= leg.startDistanceM && distanceM <= leg.endDistanceM,
    ) ?? props.analysis.legs[props.analysis.legs.length - 1]
  )
}

const resolveHoverMetricDetails = (
  properties: Partial<TrackProfileFeatureProperties> | null,
  currentDistanceM: number | null,
) => {
  if (!props.showMetricDetails) {
    return {
      segmentDistanceM: null,
      categoryLabel: null,
      rawValue: null,
    }
  }

  const hoveredLeg = resolveHoveredLeg(currentDistanceM)

  return {
    segmentDistanceM: coerceFeatureNumber(properties?.distanceM) ?? hoveredLeg?.distanceM ?? null,
    categoryLabel:
      typeof properties?.categoryLabel === 'string'
        ? properties.categoryLabel
        : (hoveredLeg?.categoryLabel ?? null),
    rawValue:
      typeof properties?.rawValue === 'string'
        ? properties.rawValue
        : (hoveredLeg?.rawValue ?? null),
  }
}

const showHoverPopup = (
  event: maplibregl.MapLayerMouseEvent,
  properties: Partial<TrackProfileFeatureProperties> | null,
) => {
  const map = mapInstance?.value
  if (!map) return

  if (!hoverPopup.value) {
    hoverPopup.value = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
      className: 'tour-popup',
      offset: 15,
    })
  }

  const currentDistanceM = getHoverCurrentDistanceM(event, properties)
  const metricDetails = resolveHoverMetricDetails(properties, currentDistanceM)
  const nearestPoint =
    currentDistanceM === null
      ? null
      : findNearestTrackProfilePoint(props.analysis.points, currentDistanceM)

  hoverPopup.value
    .setLngLat(event.lngLat)
    .setHTML(
      buildTrackHoverPopupHtml({
        title: props.hoverTitle?.trim() || 'Track',
        currentDistanceM,
        elevationM: nearestPoint?.ele ?? null,
        segmentDistanceM: metricDetails.segmentDistanceM,
        metricId: props.showMetricDetails ? props.analysis.metricId : undefined,
        categoryLabel: metricDetails.categoryLabel,
        rawValue: metricDetails.rawValue,
      }),
    )
    .addTo(map)

  try {
    const element = hoverPopup.value.getElement?.() as HTMLElement | undefined
    if (element) {
      element.style.zIndex = Z_INDEX.POPOVER.toString()
    }
  } catch {
    // Ignore popup element access errors from maplibre typings/runtime variance.
  }
}

const handleHoverMouseEnter = (layerId: string, event: maplibregl.MapLayerMouseEvent) => {
  const map = mapInstance?.value
  if (!map) return
  enteredHoverLayerIds.add(layerId)
  map.getCanvas().style.cursor = 'pointer'
  showHoverPopup(event, getHoverFeatureProperties(event))
}

const handleHoverMouseMove = (event: maplibregl.MapLayerMouseEvent) => {
  showHoverPopup(event, getHoverFeatureProperties(event))
}

const handleHoverMouseLeave = (layerId: string) => {
  enteredHoverLayerIds.delete(layerId)
  // Other bound hover layers (e.g. the wide drag-insert touch target) may still be
  // under the pointer; only hide once the pointer has left them all.
  if (enteredHoverLayerIds.size > 0) return

  const map = mapInstance?.value
  if (map) {
    map.getCanvas().style.cursor = ''
  }
  hideHoverPopup()
}

const updateLegOverlayLayer = (
  map: maplibregl.Map,
  options: {
    sourceId: string
    haloLayerId: string
    layerId: string
    arrowLayerId: string
    data: GeoJSON.FeatureCollection<GeoJSON.LineString, { color: string }>
    beforeLayerId: string | undefined
    haloWidth: number
    lineWidth: number
    haloColor?: string
  },
) => {
  const existingSource = map.getSource(options.sourceId) as maplibregl.GeoJSONSource | undefined
  if (existingSource) {
    existingSource.setData(options.data)
  } else {
    map.addSource(options.sourceId, { type: 'geojson', data: options.data })
    map.addLayer(
      {
        id: options.haloLayerId,
        type: 'line',
        source: options.sourceId,
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': options.haloColor ?? '#ffffff',
          'line-width': options.haloWidth,
          'line-opacity': 0.95,
        },
      },
      options.beforeLayerId,
    )
    map.addLayer(
      {
        id: options.layerId,
        type: 'line',
        source: options.sourceId,
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': ['get', 'color'],
          'line-width': options.lineWidth,
        },
      },
      options.beforeLayerId,
    )
    map.addLayer(
      buildTrackDirectionArrowLayer({
        id: options.arrowLayerId,
        sourceId: options.sourceId,
      }),
      options.beforeLayerId,
    )
  }

  if (
    options.beforeLayerId &&
    map.getLayer(options.arrowLayerId) &&
    map.getLayer(options.layerId) &&
    map.getLayer(options.haloLayerId) &&
    map.getLayer(options.beforeLayerId)
  ) {
    map.moveLayer(options.haloLayerId, options.beforeLayerId)
    map.moveLayer(options.layerId, options.beforeLayerId)
    map.moveLayer(options.arrowLayerId, options.beforeLayerId)
  }
}

const updateInactiveLayer = (
  map: maplibregl.Map,
  data: GeoJSON.FeatureCollection<GeoJSON.LineString, { color: string }>,
  beforeLayerId: string | undefined,
) => {
  const source = map.getSource(INACTIVE_LINE_SOURCE_ID) as maplibregl.GeoJSONSource | undefined

  if (data.features.length === 0) {
    if (map.getLayer(INACTIVE_LINE_ARROW_LAYER_ID)) map.removeLayer(INACTIVE_LINE_ARROW_LAYER_ID)
    if (map.getLayer(INACTIVE_LINE_LAYER_ID)) map.removeLayer(INACTIVE_LINE_LAYER_ID)
    if (map.getSource(INACTIVE_LINE_SOURCE_ID)) map.removeSource(INACTIVE_LINE_SOURCE_ID)
    return
  }

  if (source) {
    source.setData(data)
    return
  }

  map.addSource(INACTIVE_LINE_SOURCE_ID, { type: 'geojson', data })
  map.addLayer(
    {
      id: INACTIVE_LINE_LAYER_ID,
      type: 'line',
      source: INACTIVE_LINE_SOURCE_ID,
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': '#cbd5e1',
        'line-width': 4,
        'line-opacity': 0.78,
      },
    },
    beforeLayerId,
  )
  map.addLayer(
    buildTrackDirectionArrowLayer({
      id: INACTIVE_LINE_ARROW_LAYER_ID,
      sourceId: INACTIVE_LINE_SOURCE_ID,
    }),
    beforeLayerId,
  )

  if (
    beforeLayerId &&
    map.getLayer(INACTIVE_LINE_LAYER_ID) &&
    map.getLayer(INACTIVE_LINE_ARROW_LAYER_ID) &&
    map.getLayer(beforeLayerId)
  ) {
    map.moveLayer(INACTIVE_LINE_LAYER_ID, beforeLayerId)
    map.moveLayer(INACTIVE_LINE_ARROW_LAYER_ID, beforeLayerId)
  }
}

const removeLegOverlayLayer = (
  map: maplibregl.Map,
  sourceId: string,
  haloLayerId: string,
  layerId: string,
  arrowLayerId: string,
) => {
  if (map.getLayer(arrowLayerId)) map.removeLayer(arrowLayerId)
  if (map.getLayer(layerId)) map.removeLayer(layerId)
  if (map.getLayer(haloLayerId)) map.removeLayer(haloLayerId)
  if (map.getSource(sourceId)) map.removeSource(sourceId)
}

const updateCursorLayer = (map: maplibregl.Map, beforeLayerId: string | undefined) => {
  if (!props.cursorPoint) {
    if (map.getLayer(CURSOR_LAYER_ID)) map.removeLayer(CURSOR_LAYER_ID)
    if (map.getLayer(CURSOR_RING_LAYER_ID)) map.removeLayer(CURSOR_RING_LAYER_ID)
    if (map.getLayer(CURSOR_HALO_LAYER_ID)) map.removeLayer(CURSOR_HALO_LAYER_ID)
    if (map.getSource(CURSOR_SOURCE_ID)) map.removeSource(CURSOR_SOURCE_ID)
    return
  }

  const data: GeoJSON.FeatureCollection<GeoJSON.Point> = {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: {
          color: resolveCursorColor(),
        },
        geometry: {
          type: 'Point',
          coordinates: [props.cursorPoint.lon, props.cursorPoint.lat],
        },
      },
    ],
  }
  const source = map.getSource(CURSOR_SOURCE_ID) as maplibregl.GeoJSONSource | undefined

  if (source) {
    source.setData(data)
  } else {
    map.addSource(CURSOR_SOURCE_ID, { type: 'geojson', data })
    map.addLayer(
      {
        id: CURSOR_HALO_LAYER_ID,
        type: 'circle',
        source: CURSOR_SOURCE_ID,
        paint: {
          'circle-color': '#ffffff',
          'circle-radius': 11,
          'circle-opacity': 0.92,
        },
      },
      beforeLayerId,
    )
    map.addLayer(
      {
        id: CURSOR_RING_LAYER_ID,
        type: 'circle',
        source: CURSOR_SOURCE_ID,
        paint: {
          'circle-color': 'rgba(255,255,255,0)',
          'circle-radius': 8,
          'circle-stroke-width': 2,
          'circle-stroke-color': ['get', 'color'],
          'circle-stroke-opacity': 0.9,
        },
      },
      beforeLayerId,
    )
    map.addLayer(
      {
        id: CURSOR_LAYER_ID,
        type: 'circle',
        source: CURSOR_SOURCE_ID,
        paint: {
          'circle-color': ['get', 'color'],
          'circle-radius': 4,
          'circle-stroke-width': 1.5,
          'circle-stroke-color': '#ffffff',
        },
      },
      beforeLayerId,
    )
  }

  if (
    beforeLayerId &&
    map.getLayer(CURSOR_LAYER_ID) &&
    map.getLayer(CURSOR_RING_LAYER_ID) &&
    map.getLayer(CURSOR_HALO_LAYER_ID) &&
    map.getLayer(beforeLayerId)
  ) {
    map.moveLayer(CURSOR_HALO_LAYER_ID, beforeLayerId)
    map.moveLayer(CURSOR_RING_LAYER_ID, beforeLayerId)
    map.moveLayer(CURSOR_LAYER_ID, beforeLayerId)
  }
}

const updateHighlightedCategoryLayer = (map: maplibregl.Map, beforeLayerId: string | undefined) => {
  const highlightedCategoryId = props.highlightedCategoryId
  if (!highlightedCategoryId) {
    removeLegOverlayLayer(
      map,
      HIGHLIGHT_SOURCE_ID,
      HIGHLIGHT_HALO_LAYER_ID,
      HIGHLIGHT_LAYER_ID,
      HIGHLIGHT_ARROW_LAYER_ID,
    )
    return
  }

  const highlightedLegs = props.analysis.legs.filter(
    (leg) => leg.categoryId === highlightedCategoryId,
  )
  const data = buildTrackProfileLegFeatureCollection(highlightedLegs)
  if (data.features.length === 0) {
    removeLegOverlayLayer(
      map,
      HIGHLIGHT_SOURCE_ID,
      HIGHLIGHT_HALO_LAYER_ID,
      HIGHLIGHT_LAYER_ID,
      HIGHLIGHT_ARROW_LAYER_ID,
    )
    return
  }

  updateLegOverlayLayer(map, {
    sourceId: HIGHLIGHT_SOURCE_ID,
    haloLayerId: HIGHLIGHT_HALO_LAYER_ID,
    layerId: HIGHLIGHT_LAYER_ID,
    arrowLayerId: HIGHLIGHT_ARROW_LAYER_ID,
    data,
    beforeLayerId,
    haloWidth: 12,
    lineWidth: 8,
  })
}

const updateFocusedLegLayer = (map: maplibregl.Map, beforeLayerId: string | undefined) => {
  if (!props.focusedSegment) {
    removeLegOverlayLayer(
      map,
      FOCUSED_LEG_SOURCE_ID,
      FOCUSED_LEG_HALO_LAYER_ID,
      FOCUSED_LEG_LAYER_ID,
      FOCUSED_LEG_ARROW_LAYER_ID,
    )
    return
  }

  const data = buildTrackProfileLegFeatureCollection(props.focusedSegment.legs)
  if (data.features.length === 0) {
    removeLegOverlayLayer(
      map,
      FOCUSED_LEG_SOURCE_ID,
      FOCUSED_LEG_HALO_LAYER_ID,
      FOCUSED_LEG_LAYER_ID,
      FOCUSED_LEG_ARROW_LAYER_ID,
    )
    return
  }

  updateLegOverlayLayer(map, {
    sourceId: FOCUSED_LEG_SOURCE_ID,
    haloLayerId: FOCUSED_LEG_HALO_LAYER_ID,
    layerId: FOCUSED_LEG_LAYER_ID,
    arrowLayerId: FOCUSED_LEG_ARROW_LAYER_ID,
    data,
    beforeLayerId,
    haloWidth: 15,
    lineWidth: 10,
    haloColor: '#f8fafc',
  })
}

const updateMetricLayer = () => {
  if (!mapInstance?.value) {
    removeMetricLayers(mapInstance?.value)
    return
  }

  const map = mapInstance.value
  const beforeLayerId = resolveBeforeLayerId(map)

  // Cursor-only mode: drop every metric line/overlay and keep just the position marker.
  if (props.cursorOnly) {
    removeMetricLayers(map, { keepCursor: true })
    updateCursorLayer(map, beforeLayerId)
    return
  }

  const { activeLegs, inactiveLegs } = partitionTrackProfileLegsByRange(
    props.analysis.legs,
    props.visibleRangeM,
  )
  const data = buildTrackProfileLegFeatureCollection(activeLegs)
  const inactiveData = buildTrackProfileLegFeatureCollection(inactiveLegs)

  if (data.features.length === 0) {
    removeMetricLayers(map)
    return
  }

  const source = map.getSource(LINE_SOURCE_ID) as maplibregl.GeoJSONSource | undefined

  if (!props.renderBaseLine) {
    removeLegOverlayLayer(
      map,
      LINE_SOURCE_ID,
      LINE_HALO_LAYER_ID,
      LINE_LAYER_ID,
      LINE_ARROW_LAYER_ID,
    )
  } else if (source) {
    source.setData(data)
  } else {
    map.addSource(LINE_SOURCE_ID, { type: 'geojson', data })
    map.addLayer(
      {
        id: LINE_HALO_LAYER_ID,
        type: 'line',
        source: LINE_SOURCE_ID,
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#ffffff',
          'line-width': 7,
          'line-opacity': 0.95,
        },
      },
      beforeLayerId,
    )
    map.addLayer(
      {
        id: LINE_LAYER_ID,
        type: 'line',
        source: LINE_SOURCE_ID,
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': ['get', 'color'],
          'line-width': 4,
        },
      },
      beforeLayerId,
    )
    map.addLayer(
      buildTrackDirectionArrowLayer({
        id: LINE_ARROW_LAYER_ID,
        sourceId: LINE_SOURCE_ID,
      }),
      beforeLayerId,
    )
  }

  updateInactiveLayer(map, inactiveData, beforeLayerId)

  if (
    props.renderBaseLine &&
    beforeLayerId &&
    map.getLayer(LINE_ARROW_LAYER_ID) &&
    map.getLayer(LINE_LAYER_ID) &&
    map.getLayer(LINE_HALO_LAYER_ID) &&
    map.getLayer(beforeLayerId)
  ) {
    map.moveLayer(LINE_HALO_LAYER_ID, beforeLayerId)
    map.moveLayer(LINE_LAYER_ID, beforeLayerId)
    map.moveLayer(LINE_ARROW_LAYER_ID, beforeLayerId)
  }

  updateCursorLayer(map, beforeLayerId)
  updateHighlightedCategoryLayer(map, beforeLayerId)
  updateFocusedLegLayer(map, beforeLayerId)
}

watch(() => props.analysis, updateMetricLayer, { deep: true })
watch(() => props.cursorPoint, updateMetricLayer, { deep: true })
watch(() => props.highlightedCategoryId, updateMetricLayer)
watch(() => props.focusedSegment, updateMetricLayer, { deep: true })
watch(() => props.visibleRangeM, updateMetricLayer, { deep: true })
watch(() => props.renderBaseLine, updateMetricLayer)
watch(() => props.cursorOnly, updateMetricLayer)
watch(
  () => props.hoverLayerIds,
  () => {
    if (!boundMap) return
    syncHoverLayerListeners(boundMap)
  },
  { deep: true },
)

watch(
  () => mapInstance?.value,
  (map, previousMap) => {
    if (previousMap && previousMap !== map) {
      unbindMapListeners(previousMap)
      removeMetricLayers(previousMap)
    }
    if (!map) return
    if (boundMap !== map) {
      unbindMapListeners(boundMap)
      map.on('load', updateMetricLayer)
      map.on('styledata', updateMetricLayer)
      syncHoverLayerListeners(map)
      boundMap = map
    }
    if (map.loaded() || map.isStyleLoaded()) {
      updateMetricLayer()
    }
  },
  { immediate: true },
)

onUnmounted(() => {
  unbindMapListeners(boundMap)
  boundMap = null
  hideHoverPopup()
  removeMetricLayers(mapInstance?.value)
  uiStore.releaseTourHoverSuppression()
})
</script>

<template>
  <slot></slot>
</template>
